/**
 * Tanjong Life · the claims payment authorisation outline (tl-pr14), as it is on the shelf.
 *
 * A compact, hand-authored mirror of the seeded `process-docs.json` entry — stage names,
 * activities, performers, guards and both triggers — laid out by `outlineDoc` with the
 * geometry the authored processes use. `tests/tanjong-scenarios.test.ts` pins the mirror
 * against the seed, so a rename there fails here rather than as a canvas that no longer
 * matches the deployed process. Kept beside the scenario so each file stays under 400 lines.
 */
import { outlineDoc, tlTrigger } from './tanjong-canvas.js';

const SAS = { kind: 'agent', label: 'Settlement Assurance Sentinel' } as const;

export const PAYMENT_DOC = outlineDoc({
  pk: 'tl-pr14',
  name: 'Claims payment authorisation',
  agent: {
    name: 'Settlement Assurance Sentinel',
    role: 'Runs claims payment authorisation: reconciled to the cent, screened, released only under two signatures',
    handle: '@settlement-assurance-sentinel',
    icon: 'ledger',
    accent: 'amber',
  },
  triggers: [
    tlTrigger('trg-claims-adjudicated', 0, 'The assessor has decided; the money has not moved.'),
    tlTrigger(
      'trg-disbursements-bank-return',
      1,
      'A credit the bank returned re-enters at payee screening — the payee is read again before anything is re-issued.',
    ),
  ],
  triggerEntry: { 'trg-disbursements-bank-return': 2 },
  objective: 'tl-o3',
  metrics: ['tl-m-payment-unowned'],
  stages: [
    {
      name: 'Assemble the instruction',
      color: 'teal',
      steps: [
        {
          name: 'Open the adjudicated claim',
          type: 'application',
          app: 'claimscore',
          desc: 'The claim arrives already decided. This process starts after adjudication and never revisits it — the amount below is the assessor’s, not this workflow’s.',
        },
        {
          name: 'Assemble the settlement instruction from the adjudication',
          type: 'agent',
          performer: SAS,
          desc: 'Invokes the Claims Evidence Analyst. It assembles the instruction from the adjudication rather than from a retyped figure, because a number typed twice is a number that can differ.',
        },
        {
          name: 'Read the benefit ceiling at the event date',
          type: 'application',
          app: 'policyledger',
          desc: 'Read-only against the policy ledger — the ceiling the settlement has to sit inside, at the version in force on the event date.',
        },
        {
          name: 'Stamp the adjudication version behind the instruction',
          type: 'script',
          desc: 'Which decision this payment implements, and when that decision was taken. Without it, "why was this amount paid" is answerable only by whoever remembers.',
        },
      ],
    },
    {
      name: 'Reconcile to the adjudication',
      color: 'blue',
      steps: [
        {
          name: 'Reconcile the amount to the adjudication, to the cent',
          type: 'script',
          desc: 'Arithmetic, not judgement. The instruction and the adjudication must agree exactly — not within a tolerance, because a tolerance is a policy nobody wrote down.',
        },
        {
          name: 'Halt if the amount does not reconcile',
          type: 'script',
          guard: 'amount_reconciled',
          desc: 'A settlement that does not match its own decision stops here and goes to the claims manager. It is not rounded, and it is not released pending investigation.',
        },
        {
          name: 'Attach the adjudication and the benefit terms',
          type: 'agent',
          performer: SAS,
          desc: 'The approver sees what they are approving: the decision, the clause it rests on and the ceiling it sits inside — assembled, not summarised.',
        },
      ],
    },
    {
      name: 'Payee screening',
      color: 'amber',
      milestone: {
        name: 'Reconciled and screened',
        desc: 'The amount agrees with the decision, and both payee facts are on the record.',
        guard: 'amount_agreed',
      },
      steps: [
        {
          name: 'Read the claim payee and its change history',
          type: 'application',
          app: 'claimscore',
          desc: 'Both facts come from the ledger the carrier already keeps. Neither is new information — on the old run they were simply never read together before the money moved.',
        },
        {
          name: 'Flag an account changed inside the look-back window',
          type: 'script',
          desc: 'Thirty days before submission. This is a trigger, not evidence: accounts change for ordinary reasons, and the screen says so.',
        },
        {
          name: 'Compare the account name to the life assured',
          type: 'application',
          app: 'policyledger',
          desc: 'A mismatch is also not proof of anything. Together the two are why a person is being asked — which is a different claim, and the one this process actually makes.',
        },
        {
          name: 'Screen the payee',
          type: 'application',
          app: 'disbursements',
          desc: 'Sanctions and watchlist screening on the payee name, read from the disbursement ledger. A read, and the only operation this workflow holds on that system before the second gate.',
        },
        {
          name: 'Assemble the payee screening note',
          type: 'agent',
          performer: SAS,
          desc: 'States both facts as reasons rather than as findings, and says plainly that neither is proof on its own. An approver who is told "fraud detected" learns less than one who is told what changed and when.',
        },
      ],
    },
    {
      name: 'Dual control',
      color: 'pink',
      steps: [
        {
          name: 'Authorise the settlement',
          type: 'human',
          performer: { kind: 'group', label: 'Claims Managers' },
          sla: { amount: '8', unit: 'h' },
          desc: 'The first gate. A claims manager accepts the amount and the payee, with the adjudication and the screening note in front of them.',
        },
        {
          name: 'Re-read the payee after authorisation',
          type: 'application',
          app: 'claimscore',
          guard: 'authorise_settlement',
          desc: 'THE WINDOW. Between a settlement being authorised and being released, nothing else re-reads the receiving account — so an account changed in that gap goes out unexamined. This step closes it, and it is the first thing the Claims Integrity Sentinel was built to watch.',
        },
        {
          name: 'Release the payment',
          type: 'human',
          performer: { kind: 'group', label: 'Treasury & Release Authority' },
          sla: { amount: '4', unit: 'h' },
          desc: 'The second gate, and a different desk. Treasury releases — or does not. There is no timeout on this step that releases if nobody acts, because a payment that goes out because everyone was busy is the failure this workflow exists to prevent.',
        },
        {
          name: 'Assert the two approvers are different people',
          type: 'script',
          desc: 'Dual control that one person can satisfy twice is not dual control. On the old run the second approver was on leave, the queue was four days deep, and it went out on one signature.',
        },
      ],
    },
    {
      name: 'Release & record',
      color: 'green',
      milestone: {
        name: 'Released under dual control',
        desc: 'Two named people, on two desks, and the payee re-read between them.',
        guard: 'dual_control_held',
      },
      steps: [
        {
          name: 'Release the funds',
          type: 'application',
          app: 'disbursements',
          guard: 'release_payment',
          desc: 'The only activity on this canvas that moves money, and it cannot run until treasury has released it. No agent holds this operation.',
        },
        {
          name: 'Write one payment record',
          type: 'application',
          app: 'claimscore',
          desc: 'The amount, the adjudication behind it, both approvers, the screening note and the payee as it stood at release. One record, produced whether the payment went out or was held.',
        },
        {
          name: 'Tell the claimant the payment has been released',
          type: 'application',
          app: 'm365',
          desc: 'On the channel they used. The claimant learning from their own bank statement is how the old run’s failure was found on day thirty.',
        },
      ],
    },
  ],
});
